import { formatDateTimeCompact } from '@/lib/datetime'
import type { AdminTripEdit, TripEditChange } from '@/types/api'

/** One row per part of the booking the customer wants moved. */
export interface EditDiff {
  kind: TripEditChange
  label: string
  before: string
  after: string
}

const KIND_LABELS: Record<TripEditChange, string> = {
  TIME: 'Pickup time',
  DURATION: 'Duration',
  LOCATION: 'Location',
}

/**
 * The change, as before-and-after pairs.
 *
 * Built from the requested values rather than from the `changes` array so the
 * table and the dialog cannot show a kind with nothing under it. Locations
 * produce up to two rows — a customer may move the pickup, the destination, or
 * both, and collapsing them into one line loses which.
 *
 * The "before" is the snapshot taken when the customer asked, not the booking
 * as it stands today. Where the office has since moved the booking itself the
 * two disagree, and {@link scheduleDrifted} is what surfaces that.
 */
export function diffOf(edit: AdminTripEdit): EditDiff[] {
  const rows: EditDiff[] = []

  if (edit.requestedStartTime) {
    rows.push({
      kind: 'TIME',
      label: KIND_LABELS.TIME,
      before: formatDateTimeCompact(edit.previousStartTime),
      after: formatDateTimeCompact(edit.requestedStartTime),
    })
  }

  if (edit.requestedPackageName) {
    rows.push({
      kind: 'DURATION',
      label: KIND_LABELS.DURATION,
      before: edit.previousPackageName ?? '—',
      after: edit.requestedPackageName,
    })
  }

  if (edit.requestedPickupAddress) {
    rows.push({
      kind: 'LOCATION',
      label: 'Pickup',
      before: edit.previousPickupAddress ?? '—',
      after: edit.requestedPickupAddress,
    })
  }

  if (edit.requestedDropoffAddress) {
    rows.push({
      kind: 'LOCATION',
      label: 'Destination',
      before: edit.previousDropoffAddress ?? '—',
      after: edit.requestedDropoffAddress,
    })
  }

  return rows
}

/** A one-line summary for the table: "Pickup time, Duration". */
export function summariseChanges(edit: AdminTripEdit): string {
  if (edit.changes.length === 0) return '—'
  return edit.changes.map((change) => KIND_LABELS[change] ?? change).join(', ')
}

/**
 * Whether the booking has moved since the customer asked.
 *
 * Worth showing rather than hiding: the office can reschedule a booking for its
 * own reasons while the request sits in the queue, and a supervisor approving a
 * time change needs to know they are moving it from somewhere other than where
 * the customer thought it was.
 */
export function scheduleDrifted(edit: AdminTripEdit): boolean {
  if (!edit.previousStartTime || !edit.bookingStartTime) return false
  return edit.previousStartTime !== edit.bookingStartTime
}

/** What the state means for whoever is looking at the row. */
export function describeStatus(edit: AdminTripEdit): string {
  switch (edit.status) {
    case 'PENDING_REVIEW':
      return 'Waiting for a decision'
    case 'AWAITING_PAYMENT':
      return 'Waiting for the customer to pay'
    case 'PAYMENT_SUBMITTED':
      return 'Transfer receipt to check'
    case 'APPLIED':
      return edit.paidAt ? 'Paid and applied' : 'Applied at no extra cost'
    case 'REJECTED':
      return edit.decidedByAdminName ? `Declined by ${edit.decidedByAdminName}` : 'Declined'
    case 'WITHDRAWN':
      return 'Cancelled by the customer'
    default:
      return edit.status
  }
}
